import type Player from 'video.js/dist/types/player';
import logger from '~/services/logger';
import { isValidUrl } from '~/utils/validations.ts';
import { hideTopBarPlugin } from '../plugins/top-bar.ts';
import { getIsGoogleIma } from './use-is-google-ima.ts';

type VastVideoAds = {
  offset: 'preroll' | 'postroll' | number | undefined;
  ad_url: string | undefined;
};

export type GoogleImaPlugin = {
  tag_vmap_url: string | undefined;
  tag_preroll_url: string | undefined;
  tag_postroll_url: string | undefined;
  tag_midroll_url: string | undefined;
  midroll_offset: number | undefined;
};

export type VideoAds = (string | undefined) | VastVideoAds[] | [];

type ValidateFeedVideoAdsUrls = {
  prerollUrl: string;
  midrollUrl: string;
  midrollOffset: number;
  postrollUrl: string;
};

/**
 * Initializes the video.js advertisement workflow if the VMAP XML is available
 * @param playerID The video.js player ID
 * @param player The video.js player
 * @param vmapXml The VMAP XML string
 * @param midrollTimeOffset The midroll time offset in seconds
 * @param isPostroll A boolean indicating if postroll ad is found
 * @param setShowAdsBackButton Sets the state for the visibility of the ads back button
 * @param setShowPlayNextOnPostroll Sets the state for the behavior of the play next on postroll
 * @returns void
 */
export function videojsAdvertisement({
  playerID,
  player,
  vmapXml,
  midrollTimeOffset,
  isPostroll,
  setShowAdsBackButton,
  setShowPlayNextOnPostroll,
}: {
  playerID: string;
  player: Player;
  vmapXml: string;
  midrollTimeOffset: number;
  isPostroll: boolean;
  setShowAdsBackButton: (showAdsBackButton: boolean) => void;
  setShowPlayNextOnPostroll: (showPlayNextOnPostroll: boolean) => void;
}) {
  if (!getIsGoogleIma()) return;

  // @ts-ignore
  if (!player || !player?.ima || typeof player?.ima !== 'function') return;
  // @ts-ignore
  player.ima({
    adsResponse: vmapXml,
  });

  if (!vmapXml) {
    const adContainer = document.querySelector('.ima-ad-container');
    // @ts-ignore
    if (adContainer) adContainer.style.display = 'none';

    return;
  }

  adClickHandler({
    playerID,
    player,
  });

  player.on('readyforpreroll', function () {
    setShowAdsBackButton(true);
  });

  if (midrollTimeOffset !== 0) {
    let isMidrollPlayed: boolean = false;

    player.on('timeupdate', function () {
      if (isMidrollPlayed) return;

      if (player.currentTime() || 0 >= midrollTimeOffset) {
        setShowAdsBackButton(true);
        isMidrollPlayed = true;
      }
    });
  }

  let isPostrollPlayed: boolean = false;

  player.on('readyforpostroll', function () {
    setShowAdsBackButton(true);
    isPostrollPlayed = true;
  });

  player.on('adend', function () {
    setShowAdsBackButton(false);
    if (isPostrollPlayed) return;
    hideTopBarPlugin();
  });

  if (isPostroll) {
    setTimeout(() => {
      setShowPlayNextOnPostroll(true);
    }, 1000);
  }
}

/**
 * Gets the midroll offset from the VMAP XML
 * @param xmlString The VMAP XML string
 * @returns The midroll offset in seconds or 0
 */
export function getMidrollOffsetFromXml(xmlString: string): number {
  try {
    if (!xmlString) return 0;

    const adBreakElements: HTMLCollectionOf<Element> =
      getAdBreakElements(xmlString);

    for (let i = 0; i < adBreakElements.length; i++) {
      const breakId: string | null = adBreakElements[i].getAttribute('breakId');

      if (typeof breakId !== 'string') continue;

      if (!breakId.toLowerCase().includes('midroll')) continue;

      const timeOffset: string | null =
        adBreakElements[i].getAttribute('timeOffset');

      if (typeof timeOffset !== 'string') return 0;

      return convertMidrollOffsetToNumber(timeOffset);
    }

    return 0;
  } catch (error: any) {
    return 0;
  }
}

/**
 * Checks if the postroll break ID is in the VMAP XML
 * @param xmlString The VMAP XML string
 * @returns A boolean indicating if the postroll break ID is in the VMAP XML
 */
export function isPostrollBreakIdInXml(xmlString: string): boolean {
  try {
    if (!xmlString) return false;

    const adBreakElements: HTMLCollectionOf<Element> =
      getAdBreakElements(xmlString);

    for (let i = 0; i < adBreakElements.length; i++) {
      const breakId: string | null = adBreakElements[i].getAttribute('breakId');

      if (typeof breakId !== 'string') continue;

      if (breakId.toLowerCase().includes('postroll')) return true;
    }

    return false;
  } catch (error: any) {
    return false;
  }
}

/**
 * Gets the ad break elements from the VMAP XML
 * @param xmlString The VMAP XML string
 * @returns The ad break elements
 */
function getAdBreakElements(xmlString: string): HTMLCollectionOf<Element> {
  const parser = new DOMParser();
  const xmlDoc: Document = parser.parseFromString(xmlString, 'text/xml');
  return xmlDoc.getElementsByTagName('vmap:AdBreak');
}

/**
 * Converts the midroll offset to a number
 * @param midrollOffset The midroll offset, format: 00:00:00.000
 * @returns The midroll offset in seconds or 0
 */
export function convertMidrollOffsetToNumber(midrollOffset: string): number {
  if (typeof midrollOffset !== 'string' || !midrollOffset) return 0;

  const timeParts: string[] = midrollOffset.split(':');

  if (!Array.isArray(timeParts) || timeParts?.length === 0) return 0;

  let midrollOffsetInSeconds: number = 0;

  timeParts.forEach((part: string) => {
    midrollOffsetInSeconds = midrollOffsetInSeconds * 60 + parseFloat(part);
  });

  return midrollOffsetInSeconds;
}

/**
 * Validates the VMAP or VAST URL's plugin object
 * @param googleImaPlugin Google Interactive Media Ads QuickBrick plugin object
 * @returns The object with valid URL's or undefined for the keys with invalid URL's or midroll_offset
 */
export function validateGoogleImaPluginObject(
  googleImaPlugin: GoogleImaPlugin
): GoogleImaPlugin {
  try {
    return Object.entries(googleImaPlugin).reduce((acc: any, [key, value]) => {
      if (key === 'midroll_offset') {
        acc[key] = isNaN(Number(value)) ? undefined : value;
        return acc;
      }

      acc[key] = isValidUrl(value as string) ? value : undefined;

      return acc;
    }, {});
  } catch (error) {
    return {
      tag_vmap_url: undefined,
      tag_preroll_url: undefined,
      tag_postroll_url: undefined,
      tag_midroll_url: undefined,
      midroll_offset: undefined,
    };
  }
}

/**
 * Validates the feed video ads and returns the valid URL's
 * @param videoAds The feed video ads
 * @returns The object with valid URL's or empty strings for the keys with invalid URL's
 */
export function validateFeedVideoAdsUrls(
  videoAds: VastVideoAds[]
): ValidateFeedVideoAdsUrls {
  const validatedUrls: any = {
    prerollUrl: '',
    midrollUrl: '',
    midrollOffset: 0,
    postrollUrl: '',
  };

  if (!(Array.isArray(videoAds) && videoAds.length > 0)) return validatedUrls;

  videoAds.forEach((videoAd) => {
    const { offset, ad_url }: VastVideoAds = videoAd;

    if (!isValidUrl(ad_url)) return;

    if (typeof offset === 'number') {
      validatedUrls['midrollUrl'] = ad_url;

      if (isNaN(offset) || offset <= 0) return;

      validatedUrls['midrollOffset'] = offset;
    }

    if (typeof offset !== 'string') return;

    if (offset.toLowerCase() === 'preroll') {
      validatedUrls['prerollUrl'] = ad_url;
    }

    if (offset.toLowerCase() === 'postroll') {
      validatedUrls['postrollUrl'] = ad_url;
    }
  });

  return validatedUrls;
}

/**
 * Creates the VMAP XML
 * @param prerollUrl The preroll URL
 * @param midrollUrl The midroll URL
 * @param postrollUrl The postroll URL
 * @param midrollOffset The midroll offset
 * @returns The VMAP XML string or an empty string
 */
export function createVmapXml({
  prerollUrl,
  midrollUrl,
  postrollUrl,
  midrollOffset,
}: {
  prerollUrl: string | undefined;
  midrollUrl: string | undefined;
  postrollUrl: string | undefined;
  midrollOffset: number | undefined;
}): string {
  if (!prerollUrl && !midrollUrl && !postrollUrl) return '';

  const prerollMarkup: string = `
  <vmap:AdBreak timeOffset="start" breakType="linear" breakId="preroll">
      <vmap:AdSource id="preroll-ad-1" allowMultipleAds="false" followRedirects="true">
          <vmap:AdTagURI templateType="vast3">
              <![CDATA[${prerollUrl}]]>
          </vmap:AdTagURI>
      </vmap:AdSource>
  </vmap:AdBreak>
  `;

  const formattedMidrollOffset: string =
    convertSecondsToVMAPTimeOffset(midrollOffset);

  const midrollOffsetMarkup: string = formattedMidrollOffset
    ? `timeOffset="${formattedMidrollOffset}"`
    : '';

  const midrollMarkup: string = `
  <vmap:AdBreak ${midrollOffsetMarkup} breakType="linear" breakId="midroll">
      <vmap:AdSource id="midroll-ad-1" allowMultipleAds="false" followRedirects="true">
          <vmap:AdTagURI templateType="vast3">
              <![CDATA[${midrollUrl}]]>
          </vmap:AdTagURI>
      </vmap:AdSource>
  </vmap:AdBreak>
  `;

  const postrollMarkup: string = `
  <vmap:AdBreak timeOffset="end" breakType="linear" breakId="postroll">
      <vmap:AdSource id="postroll-ad-1" allowMultipleAds="false" followRedirects="true">
          <vmap:AdTagURI templateType="vast3">
              <![CDATA[${postrollUrl}]]>
          </vmap:AdTagURI>
      </vmap:AdSource>
  </vmap:AdBreak>
  `;

  const vmapXml: string = `
  <vmap:VMAP xmlns:vmap="http://www.iab.net/videosuite/vmap" version="1.0">
      ${prerollUrl ? prerollMarkup : ''}
      ${midrollUrl ? midrollMarkup : ''}
      ${postrollUrl ? postrollMarkup : ''}
  </vmap:VMAP>
`;

  return vmapXml;
}

/**
 * Converts seconds to VMAP time offset
 * @param offsetInSeconds The offset in seconds to convert, max 24 hours (86400 seconds)
 * @returns The VMAP time offset string or an empty string if the input is invalid or undefined
 */
export function convertSecondsToVMAPTimeOffset(
  offsetInSeconds: number | undefined
): string {
  try {
    if (!offsetInSeconds || offsetInSeconds > 86400) return '';

    const isInvalidOffset: boolean =
      offsetInSeconds <= 0 || isNaN(offsetInSeconds);

    if (typeof offsetInSeconds !== 'number' || isInvalidOffset) return '';

    const date: Date = new Date(offsetInSeconds * 1000);

    const hours: number = date.getUTCHours();
    const minutes: number = date.getUTCMinutes();
    const seconds: number = date.getUTCSeconds();

    const formattedHours: string = String(hours).padStart(2, '0');
    const formattedMinutes: string = String(minutes).padStart(2, '0');
    const formattedSeconds: string = String(seconds).padStart(2, '0');

    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return '';

    if (hours >= 24) return '';

    const vmapTimeOffset: string = `${formattedHours}:${formattedMinutes}:${formattedSeconds}.000`;

    return vmapTimeOffset;
  } catch (error: any) {
    logger.info(`convertSecondsToVMAPTimeOffset: ${error.message}`);
    return '';
  }
}

export function getAdTagUrl({
  videoAds,
  googleImaPlugin,
}: {
  videoAds: VideoAds;
  googleImaPlugin: GoogleImaPlugin;
}): string {
  if (typeof videoAds === 'string' && isValidUrl(videoAds)) return videoAds;

  const { tag_vmap_url, tag_preroll_url } =
    validateGoogleImaPluginObject(googleImaPlugin);

  if (tag_vmap_url) return tag_vmap_url;
  if (tag_preroll_url) return tag_preroll_url;

  if (Array.isArray(videoAds)) {
    const preroll = videoAds.find(
      (ad) => typeof ad === 'object' && ad.offset === 'preroll'
    ) as VastVideoAds | undefined;

    if (preroll && isValidUrl(preroll.ad_url)) return preroll.ad_url as string;
  }

  return '';
}

/**
 * Gets the VMAP XML from the feed or the plugin object
 * If the VMAP URL is provided, it fetches the VMAP XML from the URL
 * If the VAST URL's are provided, it creates the VMAP XML from the URL's
 * @param videoAds The video ads from the feed
 * @param googleImaPlugin The Google IMA plugin object
 * @returns The VMAP XML string or an empty string
 */
export async function getVmapXml({
  videoAds,
  googleImaPlugin,
}: {
  videoAds: VideoAds;
  googleImaPlugin: GoogleImaPlugin;
}): Promise<string> {
  try {
    // #region Feed
    if (typeof videoAds === 'string' && isValidUrl(videoAds)) {
      return await fetchVmapXml(videoAds);
    }

    const {
      prerollUrl,
      midrollUrl,
      midrollOffset,
      postrollUrl,
    }: ValidateFeedVideoAdsUrls = validateFeedVideoAdsUrls(
      videoAds as VastVideoAds[]
    );

    if (prerollUrl || midrollUrl || postrollUrl) {
      const vmapXmlFromFeed: string = createVmapXml({
        prerollUrl,
        midrollUrl,
        postrollUrl,
        midrollOffset,
      });

      return vmapXmlFromFeed;
    }
    // #endregion

    // #region Plugin
    const {
      tag_vmap_url,
      tag_preroll_url,
      tag_postroll_url,
      tag_midroll_url,
      midroll_offset,
    }: GoogleImaPlugin = validateGoogleImaPluginObject(googleImaPlugin);

    if (tag_vmap_url) {
      return await fetchVmapXml(tag_vmap_url);
    }

    if (!tag_preroll_url && !tag_midroll_url && !tag_postroll_url) return '';

    const vmapXmlFromPlugin: string = createVmapXml({
      prerollUrl: tag_preroll_url,
      midrollUrl: tag_midroll_url,
      postrollUrl: tag_postroll_url,
      midrollOffset: Number(midroll_offset),
    });

    return vmapXmlFromPlugin;
    // #endregion

    async function fetchVmapXml(vmapUrl: string): Promise<string> {
      try {
        const fetchVmapXml = await fetch(vmapUrl);

        if (!fetchVmapXml.ok || fetchVmapXml.status !== 200) return '';

        const vmapXml: string = await fetchVmapXml.text();

        return vmapXml;
      } catch (error) {
        return '';
      }
    }
  } catch (error) {
    return '';
  }
}

/**
 * Handles the ad click event
 * @param playerID The video.js player ID
 * @param player The video.js player
 * @returns void
 */
function adClickHandler({
  playerID,
  player,
}: {
  playerID: string;
  player: Player;
}): void {
  try {
    const adContainer: Element | null = document.querySelector(
      `.${playerID}_ima-ad-container`
    );

    if (!adContainer) return;

    adContainer.addEventListener('click', function () {
      // FIXME: ima doesn't exist
      // @ts-ignore
      player.ima.resumeAd();
    });
  } catch (error: any) {
    logger.info(`adClickHandler: ${error.message}`);
  }
}
